import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Area,
  AreaChart,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import platformAPI from "../../../services/platformApi";
import L from "../../../constants/baysportLabels";
import {
  ChartTooltipBox,
  formatRub,
  ForecastCard,
  HealthRing,
  InsightList,
  LabelWithTip,
  METRIC_TIPS,
  MetricCard,
  PlanBucketCard,
  SaasFunnel,
  SubscriptionsTable,
  TopClubsTable,
} from "./PlatformDashboardParts";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

const PIE_COLORS = ["#0f766e", "#2563eb", "#ca8a04", "#7c3aed", "#64748b"];

function formatWeekShort(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}

function formatDayShort(iso) {
  if (!iso) return "";
  return new Date(`${iso}T12:00:00`).toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
  });
}

function mergeSeriesWithForecast(actual = [], projected = []) {
  const hist = actual.map((p) => ({
    name: formatWeekShort(p.week),
    week: p.week,
    actual: p.value,
    forecast: null,
  }));
  if (!hist.length) {
    return projected.map((p) => ({
      name: formatWeekShort(p.week),
      week: p.week,
      actual: null,
      forecast: p.value,
    }));
  }
  const bridge = { ...hist[hist.length - 1], forecast: hist[hist.length - 1].actual };
  const future = projected.map((p) => ({
    name: formatWeekShort(p.week),
    week: p.week,
    actual: null,
    forecast: p.value,
  }));
  return [...hist.slice(0, -1), bridge, ...future];
}

export default function PlatformDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [payStats, setPayStats] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      platformAPI.getAnalytics(),
      platformAPI.getPaymentsStats().catch(() => null),
      platformAPI.listPayments({ limit: 8, status: "confirmed" }).catch(() => null),
    ])
      .then(([a, p, payments]) => {
        if (cancelled) return;
        setAnalytics(a.analytics || null);
        setPayStats(p?.stats || null);
        setRecentPayments(payments?.payments || []);
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e.response?.data?.error || "Не удалось загрузить сводку");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const overview = analytics?.overview || {};
  const growth = analytics?.growth || {};
  const activity = analytics?.activity || {};
  const subscriptions = analytics?.subscriptions || {};
  const forecast = analytics?.forecast || {};
  const funnel = analytics?.funnel || null;

  const signupSeries = useMemo(
    () =>
      mergeSeriesWithForecast(
        growth.signups_weekly || [],
        forecast.tenants?.projected || [],
      ),
    [growth.signups_weekly, forecast.tenants?.projected],
  );

  const sessionSeries = useMemo(
    () =>
      mergeSeriesWithForecast(
        activity.sessions_weekly || [],
        forecast.sessions?.projected || [],
      ),
    [activity.sessions_weekly, forecast.sessions?.projected],
  );

  const clientsSeries = useMemo(
    () =>
      mergeSeriesWithForecast(
        growth.clients_weekly || [],
        forecast.clients?.projected || [],
      ),
    [growth.clients_weekly, forecast.clients?.projected],
  );

  const revenueSeries = useMemo(
    () =>
      (payStats?.revenue_daily || [])
        .filter((_, i, arr) => i >= arr.length - 30)
        .map((p) => ({
          name: formatDayShort(p.day),
          amount: p.amount,
        })),
    [payStats?.revenue_daily],
  );

  const planPie = useMemo(
    () =>
      (subscriptions.by_plan || [])
        .filter((p) => p.count > 0)
        .map((p) => ({ name: p.label, value: p.count, mrr: p.mrr_rub })),
    [subscriptions.by_plan],
  );

  return (
    <div className={s.page}>
      <PlatformPageHeader
        eyebrow={`${L.productName} · платформа`}
        title="Сводка"
        lead="MRR, воронка клубов, прогнозы и риски — операционная картинка SaaS."
        actions={
          <>
            <Link to="/admin/tenants" className={s.heroActionBtn}>
              Клубы
            </Link>
            <Link to="/admin/payments" className={s.heroActionBtnGhost}>
              Оплаты
            </Link>
          </>
        }
      />

      {err ? <div className={s.alertError}>{err}</div> : null}
      {loading ? <div className={s.loading}>Загрузка аналитики…</div> : null}

      {!loading && analytics ? (
        <>
          <section className={s.metricGrid}>
            <MetricCard
              label="MRR"
              value={`${formatRub(overview.mrr_estimate_rub)} ₽`}
              hint={`ARR ~ ${formatRub(overview.arr_estimate_rub)} ₽`}
              forecast={`Прогноз 30д: ${formatRub(forecast.mrr_next_30d_rub)} ₽`}
              tip={METRIC_TIPS.mrr}
              accent
              trend={forecast?.tenants?.trend}
            />
            <MetricCard
              label="Оплаты 30 дн."
              value={`${formatRub(payStats?.revenue_30d_rub)} ₽`}
              hint={`За 7 дн.: ${formatRub(payStats?.revenue_7d_rub)} ₽`}
              tip={METRIC_TIPS.payments_30}
            />
            <MetricCard
              label="Клубы"
              value={overview.tenants_total || 0}
              hint={`Активных: ${overview.tenants_by_status?.active || 0} · Пробных: ${overview.tenants_by_status?.trial || 0}`}
              forecast={
                forecast.new_tenants_next_30d != null
                  ? `Прогноз +${forecast.new_tenants_next_30d} за месяц`
                  : null
              }
              tip={METRIC_TIPS.clubs}
            />
            <MetricCard
              label="Новые за 30 дн."
              value={growth.new_tenants_30d || 0}
              hint={`За неделю: ${growth.new_tenants_7d || 0} · Рост ${growth.growth_rate_pct || 0}%`}
              tip={METRIC_TIPS.new_30}
              trend={
                (growth.growth_rate_pct || 0) > 5
                  ? "up"
                  : (growth.growth_rate_pct || 0) < -5
                    ? "down"
                    : "flat"
              }
            />
            <article className={s.metricCard} title={METRIC_TIPS.health}>
              <LabelWithTip tip={METRIC_TIPS.health} className={s.metricLabel}>
                Здоровье платформы
              </LabelWithTip>
              <div className={s.healthMetricRow}>
                <HealthRing score={overview.health_score || 0} />
                <div className={s.metricHint}>
                  Активных клубов: {activity.active_clubs_30d || 0}
                  <br />
                  <span title={METRIC_TIPS.utilization}>
                    Утилизация: {activity.utilization_pct || 0}%
                  </span>
                  <br />
                  Спортсменов: {formatRub(overview.clients_total)}
                </div>
              </div>
            </article>
          </section>

          <section className={s.forecastGrid}>
            <ForecastCard
              label="MRR через 30 дней"
              value={formatRub(forecast.mrr_next_30d_rub)}
              suffix=" ₽"
              hint="С учётом текущего роста клубов"
              tip={METRIC_TIPS.mrr_30}
              accent
              trend={forecast.tenants?.trend}
            />
            <ForecastCard
              label="MRR через 90 дней"
              value={formatRub(forecast.mrr_next_90d_rub)}
              suffix=" ₽"
              hint={`ARR прогноз ~ ${formatRub(forecast.arr_next_rub)} ₽`}
              tip={METRIC_TIPS.mrr_90}
              trend={forecast.tenants?.trend}
            />
            <ForecastCard
              label="Новые клубы / мес"
              value={forecast.new_tenants_next_30d ?? "—"}
              hint="Линейный прогноз по 12 неделям"
              tip={METRIC_TIPS.new_clubs_forecast}
              trend={forecast.tenants?.trend}
            />
            <ForecastCard
              label="Новые спортсмены / мес"
              value={formatRub(forecast.new_clients_next_30d)}
              hint={`Сейчас за 30д: ${formatRub(activity.new_clients_30d)}`}
              tip={METRIC_TIPS.new_clients_forecast}
              trend={forecast.clients?.trend}
            />
            <ForecastCard
              label="Тренировки / мес"
              value={formatRub(forecast.sessions_next_30d)}
              hint={`Сейчас за 30д: ${formatRub(activity.sessions_30d)}`}
              tip={METRIC_TIPS.sessions_forecast}
              trend={forecast.sessions?.trend}
            />
            <ForecastCard
              label="Ожидаемые оплаты из trial"
              value={forecast.expected_paid_from_trials ?? 0}
              hint={
                forecast.trial_to_paid_rate != null
                  ? `Конверсия ~ ${forecast.trial_to_paid_rate}%`
                  : "Недостаточно данных"
              }
              tip={METRIC_TIPS.trial_paid}
            />
          </section>

          <section className={s.dashSplitFunnel}>
            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <LabelWithTip tip={METRIC_TIPS.saas_funnel} className={s.panelTitle}>
                  Воронка продаж SaaS
                </LabelWithTip>
                <span className={s.panelHint}>
                  Регистрация → оплата → удержание
                </span>
              </div>
              <SaasFunnel funnel={funnel} />
            </div>

            <div className={s.chartPanel}>
              <h3 className={s.panelTitle}>Инсайты и риски</h3>
              <InsightList items={analytics.insights || []} />
              <div className={s.atRiskRow}>
                <div className={s.atRiskItem} title={METRIC_TIPS.expiring}>
                  <strong>{analytics.at_risk?.expiring_soon || 0}</strong>
                  <span>Истекает скоро</span>
                </div>
                <div className={s.atRiskItem} title={METRIC_TIPS.inactive_trials}>
                  <strong>{analytics.at_risk?.inactive_trials || 0}</strong>
                  <span>Неактивные trial</span>
                </div>
                <div className={s.atRiskItem} title={METRIC_TIPS.churn_risk}>
                  <strong>{forecast.churn_risk_clubs || 0}</strong>
                  <span>Риск оттока</span>
                </div>
              </div>
            </div>
          </section>

          <section className={s.dashGrid}>
            <div className={`${s.chartPanel} ${s.chartPanelWide}`}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Выручка SaaS</h3>
                <Link to="/admin/payments" className={s.panelLink}>
                  Все оплаты
                </Link>
              </div>
              {revenueSeries.some((p) => p.amount > 0) ? (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={revenueSeries}>
                    <defs>
                      <linearGradient id="paRev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0f766e" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#0f766e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} width={52} axisLine={false} tickLine={false} />
                    <Tooltip
                      content={
                        <ChartTooltipBox
                          valueFormatter={(v) => `${formatRub(v)} ₽`}
                        />
                      }
                    />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      name="Выручка"
                      stroke="#0f766e"
                      fill="url(#paRev)"
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className={s.emptyHint}>Пока нет подтверждённых оплат</p>
              )}
            </div>

            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Регистрации клубов</h3>
                <span className={s.panelHint}>факт + прогноз</span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={signupSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} width={32} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltipBox />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="actual"
                    name="Факт"
                    fill="#2563eb"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={28}
                  />
                  <Line
                    type="monotone"
                    dataKey="forecast"
                    name="Прогноз"
                    stroke="#0f766e"
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    dot={{ r: 3 }}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Тренировки</h3>
                <span className={s.panelHint}>факт + прогноз</span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={sessionSeries}>
                  <defs>
                    <linearGradient id="paSes" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#2563eb" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} width={36} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltipBox />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area
                    type="monotone"
                    dataKey="actual"
                    name="Факт"
                    stroke="#2563eb"
                    fill="url(#paSes)"
                    strokeWidth={2}
                    connectNulls={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="forecast"
                    name="Прогноз"
                    stroke="#ca8a04"
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    dot={{ r: 3 }}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Новые спортсмены</h3>
                <span className={s.panelHint}>факт + прогноз</span>
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={clientsSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,23,42,0.06)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} width={36} axisLine={false} tickLine={false} />
                  <Tooltip content={<ChartTooltipBox />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar
                    dataKey="actual"
                    name="Факт"
                    fill="#0f766e"
                    radius={[6, 6, 0, 0]}
                    maxBarSize={28}
                  />
                  <Line
                    type="monotone"
                    dataKey="forecast"
                    name="Прогноз"
                    stroke="#7c3aed"
                    strokeWidth={2}
                    strokeDasharray="5 4"
                    dot={{ r: 3 }}
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className={s.chartPanel}>
              <h3 className={s.panelTitle}>Клубы по тарифам</h3>
              {planPie.length ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={planPie}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={52}
                      outerRadius={82}
                      paddingAngle={3}
                    >
                      {planPie.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      content={
                        <ChartTooltipBox
                          valueFormatter={(v, entry) =>
                            `${v} · MRR ${formatRub(entry?.payload?.mrr)} ₽`
                          }
                        />
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className={s.emptyHint}>Нет данных по тарифам</p>
              )}
              <div className={s.planBucketRow}>
                {(subscriptions.by_plan || []).map((p) => (
                  <PlanBucketCard
                    key={p.bucket}
                    bucket={p.bucket}
                    label={p.label}
                    count={p.count}
                    mrrRub={p.mrr_rub}
                  />
                ))}
              </div>
            </div>
          </section>

          <section className={s.dashSplit}>
            <div className={s.chartPanel}>
              <div className={s.panelHeaderRow}>
                <h3 className={s.panelTitle}>Последние оплаты</h3>
                <Link to="/admin/payments" className={s.panelLink}>
                  Смотреть все
                </Link>
              </div>
              {recentPayments.length ? (
                <div className={s.payList}>
                  {recentPayments.map((p) => (
                    <Link
                      key={p.id}
                      to={p.tenant_id ? `/admin/tenants/${p.tenant_id}` : "/admin/payments"}
                      className={s.payRow}
                    >
                      <div className={s.payMeta}>
                        <strong>{p.tenant_name || p.tenant_subdomain || "Клуб"}</strong>
                        <small>
                          {p.plan_name || "Тариф"} ·{" "}
                          {p.confirmed_at
                            ? new Date(p.confirmed_at).toLocaleDateString("ru-RU")
                            : "—"}
                        </small>
                      </div>
                      <span className={s.payAmount}>
                        {formatRub(p.amount)} ₽
                      </span>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className={s.emptyHint}>Подтверждённых оплат пока нет</p>
              )}
            </div>

            <TopClubsTable
              title="Топ клубов по активности"
              rows={analytics.top_clubs?.by_activity_score || []}
              valueKey="sessions_30d"
              valueLabel="зан."
            />
          </section>

          <SubscriptionsTable
            title="Подписки — истекают в ближайшие 14 дней"
            rows={subscriptions.expiring_14d || []}
            emptyText="Нет клубов с истекающей подпиской"
          />

          <div className={s.quickLinks}>
            <Link to="/admin/tenants" className={s.quickLink}>
              Все клубы
            </Link>
            <Link to="/admin/support" className={s.quickLink}>
              Поддержка
            </Link>
            <Link to="/admin/plans" className={s.quickLink}>
              Управление тарифами
            </Link>
            <Link to="/admin/audit" className={s.quickLink}>
              Журнал аудита
            </Link>
          </div>
        </>
      ) : null}
    </div>
  );
}
